/**
 * Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms 
 * and the SOW between the parties dated 10/31/2024.
 */
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
// import { USER_ID_DEV } from '../../../mock/user';
import { SafeResourceUrl } from '@angular/platform-browser';
import { isDevelopmentEnvironment } from '../../../constants';
import { USER_ID_DEV } from '../../../mock/user';
import { environment } from '../../../../environments/environment';
import { TokenPropertyService } from '../token-property.service';

export type Tile = {
  embedURL: string,
  sanitizedURL?: SafeResourceUrl,
  allProducts: string[],
  productId: string[],
  emailId?: string,
  dashboardId: string,
  sheetId: string,
  visualId: string,
  pinInd?: string,
  likeInd?: string,
  dislikeInd?: string,
  height: number,
  width: number,
  title?: string,
  elementId: string,
  contentId: string,
  // New v2 fields
  score?: number,
  favorited?: boolean,
  pinIndicator?: boolean,
  product?: string[]
}

export type TileV2 = {
  dashboardId: string,
  contentId: string,
  score: number,
  pinIndicator?: boolean,
  product?: string[],
  // Actual API response fields
  title: string,
  pinInd: string,
  likeInd: string,
  dislikeInd: string,
  sheetId: string,
  visualId: string,
  productId: string[], // API returns simple string array like ["STD"]
  persona: string[],   // Also an array based on API response
  height: string,
  width: string
}

export type EmbedUrlResponse = {
  'embedUrl': string
}

@Injectable({
  providedIn: 'root'
})
export class TilesService {
  private API_URL: string;
  private token: any;
  private tokenDetails: any;
  private idToken: any;
  constructor(
    private http: HttpClient,
    @Inject('API_URL') apiUrl: string,
    private tokenPropertyService: TokenPropertyService
  ) {
    this.API_URL = apiUrl;
    this.token = localStorage.getItem('token');
    this.idToken = localStorage.getItem('id_token');

    // Log environment configuration for debugging
    console.log(`[TilesService] Initialized with configuration`, {
      API_URL: this.API_URL,
      quicksightEmbedBaseUrl: environment.quicksightEmbedBaseUrl,
      hasToken: !!this.token,
      hasIdToken: !!this.idToken,
      production: environment.production
    });

    this.tokenDetails = this.tokenPropertyService.getAllProperties('token');
    if (!this.tokenDetails && isDevelopmentEnvironment()) {
      this.tokenDetails = { sub: USER_ID_DEV };
    }
  }

  // New method for v2 endpoint with enhanced functionality
  getTilesV2(): Observable<TileV2[]> {
    const headers = new HttpHeaders().set('Authorization', `${this.token}`).set('id_token', `${this.idToken}`);
    return this.http.get<{ tiles: TileV2[] }>(`${this.API_URL}/v2/user/tiles`, { headers }).pipe(
      map(response => {
        if (!response.tiles || !Array.isArray(response.tiles)) {
          console.error('Invalid tiles data format:', response);
          return [];
        }
        
        return response.tiles.map(tile => ({
          ...tile,
          // productId is already a simple string array from API response
          product: tile.productId || [],
          // pinIndicator is true when pinInd is 'Y' (meaning tile is favorited/pinned)
          pinIndicator: tile.pinInd === 'Y'
        }));
      }),
      catchError(this.handleError)
    );
  }

  // Method to get embed URL with caching
  getEmbedUrl(dashboardId: string, contentId: string): Observable<string> {
    const cacheKey = `embed_url_${dashboardId}_${contentId}`;
    let cachedUrlPath: string | null = null;
    
    // Try to use cached URL path if present
    try {
      cachedUrlPath = localStorage.getItem(cacheKey);
      console.log(`[TilesService] Cache retrieval for key: ${cacheKey}`, {
        hasValue: !!cachedUrlPath,
        valueLength: cachedUrlPath?.length || 0,
        cachedPath: cachedUrlPath,
      });
    } catch (error) {
      console.error(`[TilesService] Failed to retrieve cache for key: ${cacheKey}`, error);
      cachedUrlPath = null;
    }

    if (cachedUrlPath) {
      try {
        const fullUrl = environment.quicksightEmbedBaseUrl + cachedUrlPath;
        console.log(`[TilesService] Using cached URL`, {
          cacheKey,
          cachedPath: cachedUrlPath,
          baseUrl: environment.quicksightEmbedBaseUrl,
          reconstructedUrl: fullUrl,
        });

        return new Observable((observer) => {
          observer.next(fullUrl);
          observer.complete();
        });
      } catch (error) {
        console.error(`[TilesService] Failed to reconstruct URL from cache`, {
          cacheKey,
          cachedPath: cachedUrlPath,
          baseUrl: environment.quicksightEmbedBaseUrl,
          error,
        });
        // Fall through to fetch fresh URL
      }
    } else {
      console.log(
        `[TilesService] No cached URL found for key: ${cacheKey}, fetching fresh URL`
      );
    }

    // Get fresh tokens for each request with graceful fallback
    let token: string | null = null;
    let idToken: string | null = null;
    try {
      token = localStorage.getItem('token');
      idToken = localStorage.getItem('id_token');
    } catch (error) {
      console.error(`[TilesService] Failed to access localStorage for tokens`, error);
      // Fallback to tokens captured at service initialization
      token = this.token;
      idToken = this.idToken;
    }
    
    if (!token || !idToken) {
      console.error(`[TilesService] Authentication tokens not found`, { hasToken: !!token, hasIdToken: !!idToken });
      return throwError(() => new Error('Authentication tokens not found'));
    }

    console.log(`[TilesService] Fetching fresh embed URL for dashboardId: ${dashboardId}, contentId: ${contentId}`);
    const headers = new HttpHeaders().set('Authorization', `${token}`).set('id_token', `${idToken}`);
    return this.http.get<EmbedUrlResponse>(`${this.API_URL}/tiles/embed-url?dashboardId=${dashboardId}&contentId=${contentId}`, { headers }).pipe(
      map(response => {
        const embedUrl = response['embedUrl'];
        console.log(`[TilesService] Received embed URL from API`, { 
          dashboardId, 
          contentId, 
          hasEmbedUrl: !!embedUrl,
          embedUrlLength: embedUrl?.length || 0,
          embedUrl: embedUrl 
        });
        
        if (embedUrl) {
          try {
            // Continue storing in localStorage for future re-enablement
            // Extract only the path portion after the base URL for caching
            const baseUrl = environment.quicksightEmbedBaseUrl;
            if (embedUrl.startsWith(baseUrl)) {
              const urlPath = embedUrl.substring(baseUrl.length);
              // Store only the path portion (without query parameters for consistency)
              const pathToCache = urlPath.split("?")[0];
              localStorage.setItem(cacheKey, pathToCache);
              console.log(`[TilesService] Successfully cached URL path`, { 
                cacheKey, 
                originalUrl: embedUrl,
                baseUrl,
                extractedPath: urlPath,
                cachedPath: pathToCache 
              });
            } else {
              // Fallback: if URL doesn't match expected base, store the full URL without query params
              const fallbackCacheValue = embedUrl.split("?")[0];
              localStorage.setItem(cacheKey, fallbackCacheValue);
              console.warn(`[TilesService] URL doesn't match expected base, using fallback caching`, { 
                cacheKey,
                embedUrl,
                expectedBase: baseUrl,
                cachedValue: fallbackCacheValue 
              });
            }
          } catch (error) {
            console.error(`[TilesService] Failed to cache embed URL`, { 
              cacheKey, 
              embedUrl, 
              error 
            });
          }
        } else {
          console.warn(`[TilesService] No embed URL received from API`, { dashboardId, contentId, response });
        }
        
        return embedUrl;
      }),
      catchError(this.handleError)
    );
  }

  // Method to clear all cached embed URLs from localStorage
  clearEmbedUrlCache(): void {
    try {
      const totalKeys = localStorage.length;
      const keysToRemove: string[] = [];
      for (let i = 0; i < totalKeys; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('embed_url_')) {
          keysToRemove.push(key);
        }
      }
      console.log(`[TilesService] Clearing embed URL cache`, {
        totalKeys,
        embedUrlKeysFound: keysToRemove.length,
        keysToRemove
      });

      let removedCount = 0;
      let failedCount = 0;

      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
          removedCount++;
        } catch (error) {
          console.error(`[TilesService] Failed to remove cache key: ${key}`, error);
          failedCount++;
        }
      });

      console.log(`[TilesService] Cache clearing completed`, {
        removedCount,
        failedCount,
        totalAttempted: keysToRemove.length,
      });
    } catch (error) {
      console.error(`[TilesService] Failed to clear embed URL cache`, error);
    }
  }

  private sizeMapping(size: string): number {
    switch (size) {
      case 'XS':
        return 2;
      case 'S':
        return 4;
      case 'M':
        return 6;
      case 'L':
        return 8;
      case 'XL':
        return 10;
      default:
        return 6;
    }
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred';
    
    if (error.status === 0) {
      // A client-side or network error occurred.
      console.error('An error occurred:', error.error);
      errorMessage = 'Network error - please check your connection';
    } else if (error.status === 403) {
      // Authentication/authorization error
      console.error('Authentication error:', error);
      errorMessage = 'Access denied - please check your permissions';
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, body was:`, error.error
      );
      
      // Try to extract a meaningful error message
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.error?.error) {
        errorMessage = error.error.error;
      } else if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else {
        errorMessage = `HTTP ${error.status} error occurred`;
      }
    }
    
    // Return an observable with a user-facing error message.
    return throwError(() => new Error(errorMessage));
  }
}
